import {
    fetchSubscriptionChurnReport,
    Filters,
} from 'features/reports/subscriptionChurnReportSlice'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import { useCurrentCountry } from 'hooks/miscHooks'
import queryString from 'query-string'
import { useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { PROVIDER_STATUS_OPTIONS, SUBSCRIPTION_STATUS_OPTIONS } from './common/constants'
import ReportFilter, { FilterField, FilterRecord, FilterValue } from './common/ReportFilter'
import { updateUrlWithFilters } from './common/utils'

// Type conversion helper
const convertFiltersToFilterRecord = (filters: Filters): Record<string, FilterValue> => {
    return {
        status: filters.status,
        start_date: filters.start_date,
        end_date: filters.end_date,
        service_id: filters.service_id,
        provider_status: filters.provider_status,
    }
}

const SubscriptionChurnReportFilter = () => {
    const dispatch = useAppDispatch()
    const location = useLocation()
    const country = useCurrentCountry()

    // Select data from Redux store
    const { filters, pagination, isLoading } = useAppSelector(
        (state) => state.subscriptionChurnReport,
    )

    const handleUrlUpdate = useCallback(
        (field: string, value: FilterValue) => {
            // Get current query parameters to preserve existing filters
            // Use window.location.search to get the most current URL state
            const currentQuery = queryString.parse(window.location.search, {
                arrayFormat: 'bracket', // Parse bracket format arrays correctly
            })

            // Update URL with the new field value
            updateUrlWithFilters(location, field, value, currentQuery)
        },
        [location],
    )

    const handlePageChange = useCallback(
        (page: number) => {
            dispatch(fetchSubscriptionChurnReport({ filters: filters, page, country }))
        },
        [dispatch, filters, country],
    )

    const handleFetchData = useCallback(
        (newFilters: FilterRecord, page: number = 0) => {
            dispatch(fetchSubscriptionChurnReport({ filters: newFilters, page, country }))
        },
        [dispatch, country],
    )

    const fields: FilterField[] = [
        {
            key: 'service_id',
            type: 'service',
            placeholder: 'Select Service',
            width: 'min-w-[170px]',
        },
        {
            key: 'provider_status',
            type: 'select',
            placeholder: 'Provider Status',
            options: PROVIDER_STATUS_OPTIONS,
            width: 'w-[210px]',
        },
        {
            key: 'status',
            type: 'select',
            placeholder: 'Subscription Status',
            options: SUBSCRIPTION_STATUS_OPTIONS,
            width: 'w-[210px]',
        },
        {
            key: 'start_date',
            type: 'date',
            width: 'min-w-[210px]',
        },
        {
            key: 'end_date',
            type: 'date',
            width: 'min-w-[210px]',
        },
    ]

    return (
        <ReportFilter
            fields={fields}
            initialFilters={convertFiltersToFilterRecord(filters)}
            pagination={pagination}
            onPageChange={handlePageChange}
            fetchData={handleFetchData}
            isLoading={isLoading}
            onUrlUpdate={handleUrlUpdate}
        />
    )
}

export default SubscriptionChurnReportFilter
